import React from "react";
import styled from "@emotion/styled";
import { css, useTheme } from "@emotion/react";

export type FitBandKey = "none" | "poor" | "weak" | "mixed" | "good" | "excellent";
export interface FitBand {
  key: FitBandKey;
  label: string;
  threshold: number;
  fill: number;
}
export const FIT_BANDS: FitBand[] = [
  { key: "excellent", label: "Excellent fit", threshold: 90, fill: 5 },
  { key: "good", label: "Good fit", threshold: 60, fill: 4 },
  { key: "mixed", label: "Mixed fit", threshold: 35, fill: 3 },
  { key: "weak", label: "Weak fit", threshold: 15, fill: 2 },
  { key: "poor", label: "Poor fit", threshold: 0, fill: 1 },
];
const NO_FIT_BAND: FitBand = { key: "none", label: "Poor fit", threshold: 0, fill: 0 };
export function fitBand(pct: number): FitBand {
  if (pct === 0) return NO_FIT_BAND;
  return FIT_BANDS.find((b) => pct >= b.threshold) || FIT_BANDS[FIT_BANDS.length - 1];
}
export function fitTagVariant(key: FitBandKey): "light" | "linear" {
  if (key === "excellent" || key === "good") return "linear";
  return "light";
}

export function fitTier(fill: number): "weak" | "mod" | "strong" {
  return fill <= 1 ? "weak" : fill <= 3 ? "mod" : "strong";
}

const FIT_GRAD = ["#F1BC8C", "#E3A1DA"] as const;
const FIT_SPARK =
  "M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .962 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.582a.5.5 0 0 1 0 .962L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.962 0z";

export interface FitStarsProps {
  value: number;
  label?: string;
  size?: number;
  gap?: number;
}
let fitStarsCounter = 0;

export const FitStars: React.FC<FitStarsProps> = ({ value, label, size = 19, gap = 2 }) => {
  const t: any = useTheme();
  const outline = t.dark ? t.color.gray[500] : t.color.gray[300];
  const fill = Math.max(0, Math.min(5, value | 0));
  const totalW = 5 * size + 4 * gap;
  const fillW = Math.max(fill * size + (fill - 1) * gap, 1);
  const [uid] = React.useState(() => ++fitStarsCounter);
  const gradId = `fit-grad-${uid}`;
  const maskId = `fit-mask-${uid}`;
  const sc = size / 24;

  const filled: React.ReactNode[] = [];
  const outlines: React.ReactNode[] = [];
  for (let i = 1; i <= 5; i++) {
    const tf = `translate(${(i - 1) * (size + gap)},0) scale(${sc})`;
    if (i <= fill) {
      filled.push(<g key={i} transform={tf}><path d={FIT_SPARK} fill="#fff" stroke="#fff" strokeWidth={1.7} strokeLinejoin="round" /></g>);
    } else {
      outlines.push(<g key={i} transform={tf}><path d={FIT_SPARK} fill="none" stroke={outline} strokeWidth={1.7} strokeLinejoin="round" /></g>);
    }
  }

  return (
    <Styled.Inline title={label}>
      <svg width={totalW} height={size} viewBox={`0 0 ${totalW} ${size}`}>
        <defs>
          <linearGradient id={gradId} gradientUnits="userSpaceOnUse" x1={0} y1={0} x2={fillW} y2={0}>
            <stop offset="0%" stopColor={FIT_GRAD[0]} />
            <stop offset="100%" stopColor={FIT_GRAD[1]} />
          </linearGradient>
          <mask id={maskId}>{filled}</mask>
        </defs>
        <rect x={0} y={0} width={fillW} height={size} fill={`url(#${gradId})`} mask={`url(#${maskId})`} />
        {outlines}
      </svg>
    </Styled.Inline>
  );
};

export interface FitHarveyProps {
  value: number | null;
  label?: string;
  size?: number;
}
export const FitHarvey: React.FC<FitHarveyProps> = ({ value, label, size = 12 }) => {
  const t: any = useTheme();
  if (value == null) return null;
  const fill = Math.max(1, Math.min(5, value | 0));
  const tier = fitTier(fill);
  const frac = tier === "weak" ? 0 : tier === "mod" ? 0.5 : 1;
  const r = size / 2 - 1;
  const c = size / 2;
  const ring = t.dark ? t.color.gray[500] : t.color.gray[400];
  const ink = t.dark ? t.color.gray[400] : t.color.gray[300];

  let wedge: React.ReactNode = null;
  if (frac >= 1) {
    wedge = <circle cx={c} cy={c} r={r} fill={ink} />;
  } else if (frac > 0) {
    const th = frac * 2 * Math.PI;
    const ex = c + r * Math.sin(th);
    const ey = c - r * Math.cos(th);
    wedge = <path d={`M${c},${c} L${c},${c - r} A${r},${r} 0 ${frac > 0.5 ? 1 : 0},1 ${ex.toFixed(2)},${ey.toFixed(2)} Z`} fill={ink} />;
  }

  return (
    <Styled.Inline title={label}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={c} cy={c} r={r} fill="none" stroke={ring} strokeWidth={1.25} />
        {wedge}
      </svg>
    </Styled.Inline>
  );
};

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Inline = styled.span`
  label: FitIndicator;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  svg {
    display: block;
  }
`;
